import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ChevronDown, ChevronUp, Loader2, X, GripVertical } from "lucide-react";
import PostCard from "./PostCard";
import { useIsMobile } from "../../hooks/useIsMobile";
import type { RootState } from "../../store/store";

/**
 * Floating list of posts that are still being processed (e.g. captioned-video
 * generation via ZapCap). Sits in the bottom-right corner of My Schedule so
 * the user can see in-flight work without losing the calendar context.
 *
 * Uses the same `<PostCard>` component as the calendar feed so the tile
 * styling stays in lockstep.
 *
 * Mobile (<900px, the project's SideBar breakpoint):
 *  - SideBar becomes a fixed bottom bar 80px tall — bump up so the panel
 *    clears it.
 *  - Default to collapsed so the panel doesn't dominate the viewport.
 *  - Narrower fixed width that still fits two side-by-side margins.
 *
 * Interactions:
 *  - Header acts as a drag handle (pointer events, works for mouse + touch).
 *  - Close (X) button dismisses the panel locally; if new processing items
 *    arrive after dismissal, the panel comes back automatically.
 *  - Collapse chevron hides the list body but keeps the header pinned.
 */
const FloatingProcessingPosts: React.FC = () => {
    const list = useSelector((state: RootState) => state.processingPosts.list);
    const isMobile = useIsMobile(900);
    const [collapsed, setCollapsed] = useState(isMobile);

    // Soft dismiss: don't clear the slice; just hide the panel locally.
    // Re-show automatically when the list grows past the count at dismiss.
    const [dismissed, setDismissed] = useState(false);
    const [dismissedAtCount, setDismissedAtCount] = useState(0);
    useEffect(() => {
        if (dismissed && list.length > dismissedAtCount) {
            setDismissed(false);
        }
    }, [list.length, dismissed, dismissedAtCount]);

    // Drag state. `position` is null until the user drags — until then, the
    // panel uses its default bottom-right anchor.
    const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
    const dragging = useRef(false);
    const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const panelRef = useRef<HTMLDivElement>(null);

    const handleDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!panelRef.current) return;
        const rect = panelRef.current.getBoundingClientRect();
        dragOffset.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        };
        dragging.current = true;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    };

    const handleDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragging.current || !panelRef.current) return;
        const w = panelRef.current.offsetWidth;
        const h = panelRef.current.offsetHeight;
        const maxX = window.innerWidth - w;
        const maxY = window.innerHeight - h;
        const nextX = Math.max(0, Math.min(e.clientX - dragOffset.current.x, maxX));
        const nextY = Math.max(0, Math.min(e.clientY - dragOffset.current.y, maxY));
        setPosition({ x: nextX, y: nextY });
    };

    const handleDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
        dragging.current = false;
        try {
            (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
            // Pointer may already be released — ignore.
        }
    };

    if (list.length === 0 || dismissed) return null;

    const positionStyle: React.CSSProperties = position
        ? { left: position.x, top: position.y }
        : isMobile
            ? { bottom: "6rem", right: "0.75rem" }
            : { bottom: "1rem", right: "1rem" };

    return (
        <div
            ref={panelRef}
            className={`fixed z-40 max-w-[calc(100vw-2rem)] ${
                isMobile ? "w-64" : "w-72"
            }`}
            style={positionStyle}
        >
            <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                <div
                    onPointerDown={handleDragStart}
                    onPointerMove={handleDragMove}
                    onPointerUp={handleDragEnd}
                    onPointerCancel={handleDragEnd}
                    className="w-full flex items-center justify-between p-3 bg-primary text-white text-sm font-semibold cursor-grab active:cursor-grabbing select-none touch-none"
                >
                    <span className="flex items-center gap-2 min-w-0">
                        <GripVertical className="w-4 h-4 flex-shrink-0 opacity-70" />
                        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                        <span className="truncate">Processing ({list.length})</span>
                    </span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                        <button
                            type="button"
                            aria-label={collapsed ? "Expand" : "Collapse"}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                setCollapsed((c) => !c);
                            }}
                            className="p-1 rounded hover:bg-white/10"
                        >
                            {collapsed ? (
                                <ChevronUp className="w-4 h-4" />
                            ) : (
                                <ChevronDown className="w-4 h-4" />
                            )}
                        </button>
                        <button
                            type="button"
                            aria-label="Close"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                setDismissedAtCount(list.length);
                                setDismissed(true);
                            }}
                            className="p-1 rounded hover:bg-white/10"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </span>
                </div>
                {!collapsed && (
                    <div
                        className={`overflow-y-auto p-2 bg-gray-50 ${
                            // Desktop caps at ~3.5 PostCards (each ~96px tall
                            // including mb-2 gap) so a 4th card peeks at the
                            // bottom to signal scroll. Mobile uses vh-based
                            // cap so it doesn't overshoot small viewports.
                            isMobile ? "max-h-[40vh]" : "max-h-[22rem]"
                        }`}
                    >
                        {list.map((post, idx) => (
                            <PostCard
                                key={post.id}
                                post={post}
                                idx={idx}
                                date="processing"
                                processingPreview
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FloatingProcessingPosts;
